import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

/* ── Constants ───────────────────────────────────────────────── */
const TYPES = [
  'custom_synthesis','analytical_testing','method_development',
  'stability_study','cro_service','regulatory_support',
  'technical_consultation','quality_assurance','validation',
  'calibration','training','installation','maintenance','other',
];
const PRIORITIES = ['low','medium','high','urgent'];
const STATUSES  = ['open','assigned','in_progress','pending_customer','resolved','closed','cancelled'];

const typeLabels = {
  custom_synthesis:      'Custom Synthesis',
  analytical_testing:    'Analytical Testing',
  method_development:    'Method Development',
  stability_study:       'Stability Study',
  cro_service:           'CRO Service',
  regulatory_support:    'Regulatory Support',
  technical_consultation:'Technical Consultation',
  quality_assurance:     'Quality Assurance',
  validation:            'Validation',
  calibration:           'Calibration',
  training:              'Training',
  installation:          'Installation',
  maintenance:           'Maintenance',
  other:                 'Other',
};
const statusColors   = { open:'info', assigned:'primary', in_progress:'warning', pending_customer:'warning', resolved:'success', closed:'neutral', cancelled:'error' };
const statusLabels   = { open:'Open', assigned:'Assigned', in_progress:'In Progress', pending_customer:'Pending Customer', resolved:'Resolved', closed:'Closed', cancelled:'Cancelled' };

const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const isPriv  = (role) => role === 'admin' || role === 'manager';

const emptyForm = () => ({
  title:'', description:'', type:'other', priority:'medium', status:'open',
  client_id:'', customer_id:'', assigned_to:'', due_date:'', notes:'',
  resolution_notes:'',
});

/* ── Status badge ─────────────────────────────────────────────── */
const Badge = ({ color, children }) => (
  <span className={`badge badge-${color}`} style={{ textTransform:'capitalize', fontSize:'0.72rem' }}>{children}</span>
);

/* ── Priority dot ─────────────────────────────────────────────── */
const PriDot = ({ p }) => {
  const colors = { low:'#6b7280', medium:'#3b82f6', high:'#f59e0b', urgent:'#ef4444' };
  return <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background: colors[p], marginRight:5 }} />;
};

export default function ServiceRequests() {
  const { user } = useAuth();

  /* ── List state ─────────────────────────────────────────────── */
  const [data,    setData]    = useState({ data:[], total:0, page:1, limit:25 });
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);

  /* ── Filters ────────────────────────────────────────────────── */
  const [search,     setSearch]     = useState('');
  const [fStatus,    setFStatus]    = useState('');
  const [fPriority,  setFPriority]  = useState('');
  const [fType,      setFType]      = useState('');
  const [fAssigned,  setFAssigned]  = useState('');

  /* ── Modal ──────────────────────────────────────────────────── */
  const [modal,   setModal]   = useState({ open:false, mode:'add', editId:null });
  const [form,    setForm]    = useState(emptyForm());
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  /* ── View ───────────────────────────────────────────────────── */
  const [viewModal, setViewModal] = useState({ open:false, sr:null });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  /* ── Ref data ───────────────────────────────────────────────── */
  const [clients,   setClients]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);

  const debouncedSearch = useDebounce(search, 300);

  /* ── Load list ──────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (debouncedSearch) params.search     = debouncedSearch;
      if (fStatus)   params.status     = fStatus;
      if (fPriority) params.priority   = fPriority;
      if (fType)     params.type       = fType;
      if (fAssigned) params.assigned_to = fAssigned;
      const { data: res } = await api.get('/service-requests', { params });
      setData(res);
    } catch { /* handled by interceptor */ }
    finally { setLoading(false); }
  }, [page, debouncedSearch, fStatus, fPriority, fType, fAssigned]);

  useEffect(() => { load(); }, [load]);

  /* ── Load stats ─────────────────────────────────────────────── */
  useEffect(() => {
    api.get('/service-requests/stats').then(r => setStats(r.data.data)).catch(() => {});
  }, [data]);

  /* ── Load ref data for form ─────────────────────────────────── */
  const loadFormData = async () => {
    try {
      const [cl, cu, em] = await Promise.all([
        api.get('/clients',   { params:{ limit:200, approval_status:'approved' } }),
        api.get('/customers', { params:{ limit:200 } }),
        api.get('/hr/employees'),
      ]);
      setClients(cl.data.data || []);
      setCustomers(cu.data.data || []);
      setEmployees(em.data.data || []);
    } catch { setError('Failed to load form data.'); }
  };

  /* ── Open modals ─────────────────────────────────────────────── */
  const openAdd = async () => {
    await loadFormData();
    setForm(emptyForm());
    setError('');
    setModal({ open:true, mode:'add', editId:null });
  };

  const openEdit = async (sr) => {
    await loadFormData();
    setForm({
      title:            sr.title || '',
      description:      sr.description || '',
      type:             sr.type || 'other',
      priority:         sr.priority || 'medium',
      status:           sr.status || 'open',
      client_id:        sr.client_id || '',
      customer_id:      sr.customer_id || '',
      assigned_to:      sr.assigned_to || '',
      due_date:         sr.due_date ? sr.due_date.split('T')[0] : '',
      notes:            sr.notes || '',
      resolution_notes: sr.resolution_notes || '',
    });
    setError('');
    setModal({ open:true, mode:'edit', editId:sr.id });
  };

  /* ── Save ────────────────────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        client_id:   form.client_id   || null,
        customer_id: form.customer_id || null,
        assigned_to: form.assigned_to || null,
        due_date:    form.due_date    || null,
      };
      if (modal.mode === 'add') {
        await api.post('/service-requests', payload);
      } else {
        await api.put(`/service-requests/${modal.editId}`, payload);
      }
      setModal({ open:false, mode:'add', editId:null });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  /* ── Delete (Freeze) ─────────────────────────────────────────── */
  const handleDelete = async () => {
    try {
      await api.delete(`/service-requests/${deleteConfirm.id}`);
      setDeleteConfirm(null); load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to freeze.');
    }
  };

  const handleUnfreeze = async (id) => {
    try { await api.patch(`/service-requests/${id}/unfreeze`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to unfreeze.'); }
  };

  /* ── Quick status update ─────────────────────────────────────── */
  const quickStatus = async (sr, newStatus) => {
    try {
      await api.put(`/service-requests/${sr.id}`, { status: newStatus });
      load();
    } catch { alert('Failed to update status.'); }
  };

  /* ─────────────────────────────────────────────────────────────── */

  const isAdmin = isPriv(user?.role);

  /* ── Summary cards ───────────────────────────────────────────── */
  const summaryCards = stats ? [
    { label:'Open',        value: stats.open,       color:'#3b82f6' },
    { label:'In Progress', value: Number(stats.assigned) + Number(stats.in_progress), color:'#f59e0b' },
    { label:'Resolved',    value: Number(stats.resolved) + Number(stats.closed),      color:'#10b981' },
    { label:'Urgent',      value: stats.urgent,    color:'#ef4444' },
    { label:'High',        value: stats.high_prio, color:'#f59e0b' },
    { label:'Overdue',     value: stats.overdue,    color:'#dc2626' },
  ] : [];

  return (
    <div className="page">
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Service Requests</h1>
          <p className="page-subtitle">Track and manage service requests assigned to employees</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Request
        </button>
      </div>

      {/* ── Summary cards ────────────────────────────────────────── */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginBottom:20 }}>
          {summaryCards.map(c => (
            <div key={c.label} className="card" style={{ padding:'14px 16px', textAlign:'center', borderTop:`3px solid ${c.color}` }}>
              <div style={{ fontSize:'1.6rem', fontWeight:700, color:c.color }}>{c.value}</div>
              <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────────── */}
      <div className="card" style={{ padding:'14px 16px', marginBottom:16 }}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:10, alignItems:'center' }}>
          <input className="input" placeholder="Search SR#, title, client…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ flex:'1 1 200px', minWidth:160 }} />

          <select className="input" value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }} style={{ flex:'0 0 150px' }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
          </select>

          <select className="input" value={fPriority} onChange={e => { setFPriority(e.target.value); setPage(1); }} style={{ flex:'0 0 140px' }}>
            <option value="">All Priorities</option>
            {PRIORITIES.map(p => <option key={p} value={p} style={{ textTransform:'capitalize' }}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
          </select>

          <select className="input" value={fType} onChange={e => { setFType(e.target.value); setPage(1); }} style={{ flex:'0 0 150px' }}>
            <option value="">All Types</option>
            {TYPES.map(t => <option key={t} value={t}>{typeLabels[t]}</option>)}
          </select>

          {(fStatus || fPriority || fType || search) && (
            <button className="btn btn-secondary" onClick={() => { setSearch(''); setFStatus(''); setFPriority(''); setFType(''); setFAssigned(''); setPage(1); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────── */}
      <div className="card">
        {loading ? (
          <div style={{ padding:40, textAlign:'center' }}><div className="spinner" /></div>
        ) : data.data.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>No service requests found.</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>SR #</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Client / Customer</th>
                  <th>Assigned To</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map(sr => (
                  <tr key={sr.id}>
                    <td>
                      <button className="btn-link" onClick={() => setViewModal({ open:true, sr })}
                        style={{ fontWeight:600, fontFamily:'monospace', fontSize:'0.8rem' }}>
                        {sr.sr_number}
                      </button>
                      {sr.is_active === 0 && <span className="badge badge-neutral" style={{ fontSize:'0.62rem', marginLeft:6 }}>Frozen</span>}
                    </td>
                    <td>
                      <div style={{ fontWeight:500, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sr.title}</div>
                      {sr.reported_by_name && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>by {sr.reported_by_name}</div>}
                    </td>
                    <td><span style={{ fontSize:'0.78rem', color:'var(--text-secondary)', textTransform:'capitalize' }}>{typeLabels[sr.type]}</span></td>
                    <td>
                      <span style={{ fontSize:'0.8rem', display:'flex', alignItems:'center' }}>
                        <PriDot p={sr.priority} />
                        <span style={{ textTransform:'capitalize' }}>{sr.priority}</span>
                      </span>
                    </td>
                    <td><Badge color={statusColors[sr.status]}>{statusLabels[sr.status]}</Badge></td>
                    <td>
                      <div style={{ fontSize:'0.82rem' }}>
                        {sr.client_name || sr.customer_name || <span style={{ color:'var(--text-muted)' }}>—</span>}
                      </div>
                    </td>
                    <td>
                      {sr.assigned_to_name
                        ? <div><div style={{ fontSize:'0.82rem', fontWeight:500 }}>{sr.assigned_to_name}</div>
                            <div style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>{sr.assigned_to_dept}</div></div>
                        : <span style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>Unassigned</span>}
                    </td>
                    <td>
                      <span style={{ fontSize:'0.82rem', color: sr.due_date && new Date(sr.due_date) < new Date() && !['resolved','closed','cancelled'].includes(sr.status) ? '#ef4444' : 'inherit' }}>
                        {fmt(sr.due_date)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(sr)}>Edit</button>
                        {/* Quick status transitions */}
                        {sr.status === 'open' && isAdmin && (
                          <button className="btn btn-sm" style={{ background:'#f59e0b', color:'#fff', border:'none' }}
                            onClick={() => quickStatus(sr, 'in_progress')}>Start</button>
                        )}
                        {sr.status === 'in_progress' && (
                          <button className="btn btn-sm" style={{ background:'#10b981', color:'#fff', border:'none' }}
                            onClick={() => quickStatus(sr, 'resolved')}>Resolve</button>
                        )}
                        {isAdmin && sr.is_active === 0 && (
                          <button className="btn btn-success btn-sm" onClick={() => handleUnfreeze(sr.id)}>Unfreeze</button>
                        )}
                        {isAdmin && sr.is_active !== 0 && (
                          <button className="btn btn-danger btn-sm" onClick={() => setDeleteConfirm(sr)}>Freeze</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />
      </div>

      {/* ── View Modal ───────────────────────────────────────────── */}
      {viewModal.open && viewModal.sr && (
        <Modal open={viewModal.open} title={`${viewModal.sr.sr_number} — ${viewModal.sr.title}`} onClose={() => setViewModal({ open:false, sr:null })}>
          <div style={{ display:'grid', gap:14 }}>
            {/* Status + Priority row */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <Badge color={statusColors[viewModal.sr.status]}>{statusLabels[viewModal.sr.status]}</Badge>
              <span style={{ display:'flex', alignItems:'center', fontSize:'0.82rem' }}>
                <PriDot p={viewModal.sr.priority} />{viewModal.sr.priority}
              </span>
              <span style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>{typeLabels[viewModal.sr.type]}</span>
            </div>

            {viewModal.sr.description && (
              <div><div style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4 }}>DESCRIPTION</div>
                <p style={{ margin:0, whiteSpace:'pre-wrap', fontSize:'0.88rem' }}>{viewModal.sr.description}</p></div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Field label="Client"       value={viewModal.sr.client_name   || viewModal.sr.customer_name || '—'} />
              <Field label="Assigned To"  value={viewModal.sr.assigned_to_name ? `${viewModal.sr.assigned_to_name} (${viewModal.sr.assigned_to_dept || ''})` : 'Unassigned'} />
              <Field label="Reported By"  value={viewModal.sr.reported_by_name || '—'} />
              <Field label="Due Date"     value={fmt(viewModal.sr.due_date)} />
              <Field label="Created"      value={fmt(viewModal.sr.created_at)} />
              <Field label="Resolved"     value={fmt(viewModal.sr.resolved_at)} />
            </div>

            {viewModal.sr.notes && (
              <div><div style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4 }}>NOTES</div>
                <p style={{ margin:0, whiteSpace:'pre-wrap', fontSize:'0.88rem' }}>{viewModal.sr.notes}</p></div>
            )}
            {viewModal.sr.resolution_notes && (
              <div style={{ background:'var(--surface-2)', borderRadius:8, padding:'10px 14px' }}>
                <div style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4 }}>RESOLUTION NOTES</div>
                <p style={{ margin:0, whiteSpace:'pre-wrap', fontSize:'0.88rem' }}>{viewModal.sr.resolution_notes}</p>
              </div>
            )}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setViewModal({ open:false, sr:null }); openEdit(viewModal.sr); }}>Edit</button>
              <button className="btn btn-secondary" onClick={() => setViewModal({ open:false, sr:null })}>Close</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Add / Edit Modal ─────────────────────────────────────── */}
      {modal.open && (
        <Modal open={modal.open} size="lg" title={modal.mode === 'add' ? 'New Service Request' : 'Edit Service Request'}
          onClose={() => setModal({ open:false, mode:'add', editId:null })}>
          <div style={{ display:'grid', gap:20 }}>
            {error && (
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'var(--radius-sm)', padding:'10px 14px', color:'#dc2626', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:8 }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            {/* Section: Basic Info */}
            <div style={{ background:'var(--surface-2)', borderRadius:'var(--radius)', padding:'16px 18px', display:'grid', gap:14 }}>
              <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Basic Info</div>

              <div>
                <label className="form-label">Title <span style={{ color:'#ef4444' }}>*</span></label>
                <input className="form-control" value={form.title}
                  onChange={e => setForm(p => ({ ...p, title:e.target.value }))}
                  placeholder="Brief description of the issue" />
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label className="form-label">Type</label>
                  <select className="form-control" value={form.type} onChange={e => setForm(p => ({ ...p, type:e.target.value }))}>
                    {TYPES.map(t => <option key={t} value={t}>{typeLabels[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Priority</label>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:4 }}>
                    {PRIORITIES.map(p => {
                      const colors = { low:'#6b7280', medium:'#3b82f6', high:'#f59e0b', urgent:'#ef4444' };
                      const sel = form.priority === p;
                      return (
                        <button key={p} type="button"
                          onClick={() => setForm(f => ({ ...f, priority:p }))}
                          style={{
                            padding:'5px 12px', borderRadius:20, border:`1.5px solid ${sel ? colors[p] : 'var(--border-dark)'}`,
                            background: sel ? colors[p] : 'var(--surface)', color: sel ? '#fff' : 'var(--text-secondary)',
                            fontSize:'0.78rem', fontWeight:sel?600:400, cursor:'pointer', transition:'all 0.12s', textTransform:'capitalize',
                          }}>
                          {p}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={3} value={form.description}
                  onChange={e => setForm(p => ({ ...p, description:e.target.value }))}
                  placeholder="Detailed description of the request…"
                  style={{ resize:'vertical' }} />
              </div>
            </div>

            {/* Section: Assignment */}
            <div style={{ background:'var(--surface-2)', borderRadius:'var(--radius)', padding:'16px 18px', display:'grid', gap:14 }}>
              <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Assignment & Status</div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label className="form-label">Assign to Employee</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to:e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {employees.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.first_name} {e.last_name || ''}{e.department ? ` · ${e.department}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <select className="form-control" value={form.status} onChange={e => setForm(p => ({ ...p, status:e.target.value }))}>
                    {STATUSES.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <div>
                  <label className="form-label">Client</label>
                  <select className="form-control" value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id:e.target.value, customer_id:'' }))}>
                    <option value="">— None —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Customer</label>
                  <select className="form-control" value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id:e.target.value, client_id:'' }))}>
                    <option value="">— None —</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Due Date</label>
                  <input className="form-control" type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date:e.target.value }))} />
                </div>
              </div>
            </div>

            {/* Section: Notes */}
            <div style={{ background:'var(--surface-2)', borderRadius:'var(--radius)', padding:'16px 18px', display:'grid', gap:14 }}>
              <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Notes</div>

              <div>
                <label className="form-label">Internal Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes:e.target.value }))}
                  placeholder="Visible only to team members…"
                  style={{ resize:'vertical' }} />
              </div>

              {(form.status === 'resolved' || form.status === 'closed') && (
                <div>
                  <label className="form-label">Resolution Notes</label>
                  <textarea className="form-control" rows={3} value={form.resolution_notes}
                    onChange={e => setForm(p => ({ ...p, resolution_notes:e.target.value }))}
                    placeholder="How was this resolved?"
                    style={{ resize:'vertical', borderColor:'#10b981' }} />
                </div>
              )}
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', paddingTop:4 }}>
              <button className="btn btn-secondary" onClick={() => setModal({ open:false, mode:'add', editId:null })}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ minWidth:130 }}>
                {saving
                  ? <span style={{ display:'flex', alignItems:'center', gap:7 }}><span className="spinner" style={{ width:14, height:14, borderWidth:2 }} /> Saving…</span>
                  : modal.mode === 'add' ? 'Create Request' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete confirm ───────────────────────────────────────── */}
      {deleteConfirm && (
        <Modal open={!!deleteConfirm} title="Freeze Service Request" onClose={() => setDeleteConfirm(null)}>
          <p>Are you sure you want to freeze <strong>{deleteConfirm.sr_number}</strong> — "{deleteConfirm.title}"? The record will be locked. You can unfreeze it later.</p>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleDelete}>Freeze</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Small helper ─────────────────────────────────────────────── */
function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'var(--text-muted)', marginBottom:2 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize:'0.88rem' }}>{value || '—'}</div>
    </div>
  );
}
